import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Zap, IndianRupee, Headphones, Tv, Infinity as InfinityIcon,
  Wifi, CheckCircle2, TrendingUp, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { featuredPlans, whyChooseUs, ottPlatformsWithLogos } from '../mock';
import { trackButtonClick, trackPlanSelection } from '../utils/analytics';
import useScrollReveal from '../hooks/useScrollReveal';

const M_LOGO_URL = 'https://customer-assets.emergentagent.com/job_webfiber/artifacts/litkh7yj_customcolor_icon_transparent_background.png';

/* Detailed satellite SVG — body, solar panel wings, dish, antenna, signal bars */
const SatelliteIcon = ({ className = '' }) => (
  <svg viewBox="0 0 64 64" className={className} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="satBody" x1="0" x2="1" y1="0" y2="1">
        <stop offset="0%" stopColor="#fff3d6" />
        <stop offset="60%" stopColor="#ffb347" />
        <stop offset="100%" stopColor="#b34400" />
      </linearGradient>
      <linearGradient id="satPanel" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor="#1a1a2e" />
        <stop offset="100%" stopColor="#0a0a1a" />
      </linearGradient>
    </defs>
    {/* Left solar panel */}
    <rect x="2" y="22" width="18" height="20" rx="1.5" fill="url(#satPanel)" stroke="#ff9a3c" strokeWidth="1" />
    <line x1="6" y1="22" x2="6" y2="42" stroke="#ff9a3c" strokeOpacity="0.45" strokeWidth="0.5" />
    <line x1="11" y1="22" x2="11" y2="42" stroke="#ff9a3c" strokeOpacity="0.45" strokeWidth="0.5" />
    <line x1="16" y1="22" x2="16" y2="42" stroke="#ff9a3c" strokeOpacity="0.45" strokeWidth="0.5" />
    {/* Right solar panel */}
    <rect x="44" y="22" width="18" height="20" rx="1.5" fill="url(#satPanel)" stroke="#ff9a3c" strokeWidth="1" />
    <line x1="48" y1="22" x2="48" y2="42" stroke="#ff9a3c" strokeOpacity="0.45" strokeWidth="0.5" />
    <line x1="53" y1="22" x2="53" y2="42" stroke="#ff9a3c" strokeOpacity="0.45" strokeWidth="0.5" />
    <line x1="58" y1="22" x2="58" y2="42" stroke="#ff9a3c" strokeOpacity="0.45" strokeWidth="0.5" />
    {/* Connector arms */}
    <rect x="20" y="30" width="4" height="4" fill="#3a2a1a" />
    <rect x="40" y="30" width="4" height="4" fill="#3a2a1a" />
    {/* Main body */}
    <rect x="24" y="22" width="16" height="20" rx="2" fill="url(#satBody)" stroke="#7a3000" strokeWidth="1" />
    <circle cx="32" cy="32" r="3" fill="#1a0a00" stroke="#ffd07a" strokeWidth="1" />
    {/* Antenna dish on top */}
    <path d="M 28 22 L 32 12 L 36 22 Z" fill="url(#satBody)" stroke="#7a3000" strokeWidth="0.8" />
    <circle cx="32" cy="10" r="2" fill="#ffd07a" stroke="#7a3000" strokeWidth="0.8" />
    <line x1="32" y1="8" x2="32" y2="4" stroke="#ffd07a" strokeWidth="1.2" />
    {/* Signal bars from antenna */}
    <path d="M 38 6 Q 42 4 44 8" stroke="#ffb347" strokeWidth="1" fill="none" opacity="0.9" />
    <path d="M 39 3 Q 45 1 49 6" stroke="#ffb347" strokeWidth="1" fill="none" opacity="0.6" />
    <path d="M 40 0 Q 48 -2 54 4" stroke="#ffb347" strokeWidth="1" fill="none" opacity="0.35" />
  </svg>
);

/* Smaller relay/probe icon for the inner orbit */
const RelayIcon = ({ className = '' }) => (
  <svg viewBox="0 0 32 32" className={className} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <radialGradient id="relayGlow" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0%" stopColor="#fff3d6" />
        <stop offset="60%" stopColor="#ff8a00" />
        <stop offset="100%" stopColor="#7a3000" />
      </radialGradient>
    </defs>
    <circle cx="16" cy="16" r="6" fill="url(#relayGlow)" stroke="#7a3000" strokeWidth="1" />
    <circle cx="16" cy="16" r="2" fill="#1a0a00" />
    <path d="M 4 16 Q 10 10 16 16" stroke="#ffb347" strokeWidth="1.5" fill="none" />
    <path d="M 28 16 Q 22 22 16 16" stroke="#ffb347" strokeWidth="1.5" fill="none" />
    <path d="M 16 4 Q 22 10 16 16" stroke="#ffb347" strokeWidth="1.5" fill="none" />
    <path d="M 16 28 Q 10 22 16 16" stroke="#ffb347" strokeWidth="1.5" fill="none" />
  </svg>
);

const Home = () => {
  useScrollReveal();

  // Mouse-tracked parallax on the M logo (subtle tilt + shift)
  const sceneRef = useRef(null);
  const logoRef = useRef(null);
  useEffect(() => {
    const scene = sceneRef.current;
    const logo = logoRef.current;
    if (!scene || !logo) return;

    let rafId = null;
    const handleMove = (e) => {
      const rect = scene.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      // Normalised offset in [-1, 1]
      const nx = Math.max(-1, Math.min(1, (e.clientX - cx) / (window.innerWidth / 2)));
      const ny = Math.max(-1, Math.min(1, (e.clientY - cy) / (window.innerHeight / 2)));

      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        // Tilt + translate the logo
        const tx = nx * 14;          // px
        const ty = ny * 10;          // px
        const rx = -ny * 8;          // deg (invert for natural tilt)
        const ry = nx * 10;          // deg
        logo.style.transform =
          `translate(-50%, -50%) translate3d(${tx}px, ${ty}px, 0) rotateX(${rx}deg) rotateY(${ry}deg)`;
      });
    };

    const handleLeave = () => {
      if (rafId) cancelAnimationFrame(rafId);
      logo.style.transform = 'translate(-50%, -50%)';
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseleave', handleLeave);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseleave', handleLeave);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  const iconMap = { Zap, IndianRupee, Headphones, Tv, Infinity: InfinityIcon, Wifi };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="home-page">
      {/* ============== HERO — Satellite + M Logo + Arcs ============== */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        {/* Ambient orange/yellow glow */}
        <div className="ambient-glow" />
        <div className="grain-overlay" />

        {/* Floating particles */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-2 h-2 bg-orange-300/50 rounded-full animate-float" />
          <div className="absolute top-40 right-20 w-1.5 h-1.5 bg-yellow-200/60 rounded-full animate-float-delayed" />
          <div className="absolute bottom-32 left-1/4 w-3 h-3 bg-orange-400/30 rounded-full animate-float" />
          <div className="absolute top-60 right-1/3 w-1 h-1 bg-yellow-100/70 rounded-full animate-float-delayed" />
          <div className="speed-lines" />
        </div>

        <div className="container mx-auto px-4 pt-24 pb-32 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left — Headline */}
            <div className="space-y-6 reveal-left">
              <Badge
                className="bg-gradient-to-r from-orange-500/30 to-amber-400/20 border border-orange-400/40 text-orange-200 backdrop-blur-md px-4 py-2 text-sm tracking-wide"
                data-testid="hero-badge"
              >
                <Sparkles className="w-4 h-4 mr-2 inline" />
                Best Price Challenge
              </Badge>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05]" data-testid="hero-headline">
                Internet at
                <span className="block mt-2 bg-gradient-to-r from-[#FFD27A] via-[#FF8C00] to-[#FF5C00] bg-clip-text text-transparent">
                  Lightning Speed
                </span>
              </h1>

              <p className="text-lg md:text-xl text-white/70 leading-relaxed max-w-xl">
                Blazing fast broadband up to{' '}
                <span className="font-bold text-2xl text-[#FFB347]">300 Mbps</span>
                {' '}with unlimited data, satellite-grade reliability and zero throttling.
              </p>

              <div className="glass-orange rounded-2xl p-4 inline-block">
                <p className="text-white font-semibold flex items-center gap-2">
                  <Tv className="w-5 h-5 text-orange-300" />
                  18+ OTT Platforms · 350+ Live TV Channels
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <Link to="/plans">
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-[#FF8C00] to-[#FFB800] hover:from-[#FF7700] hover:to-[#FFA600] text-black font-semibold px-8 shadow-[0_8px_32px_-8px_rgba(255,140,0,0.7)] hover:shadow-[0_12px_40px_-8px_rgba(255,140,0,0.9)] transition-all hover:scale-105"
                    onClick={() => trackButtonClick('View All Plans', 'Hero Section')}
                    data-testid="hero-view-plans-btn"
                  >
                    View All Plans
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-orange-400/40 bg-white/5 text-orange-100 hover:bg-orange-500/10 hover:border-orange-400 backdrop-blur-md px-8 font-semibold"
                    onClick={() => trackButtonClick('Get Started', 'Hero Section')}
                    data-testid="hero-get-started-btn"
                  >
                    Get Started
                  </Button>
                </Link>
              </div>

              <div className="pt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/70">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-orange-400" />
                  <span>Unlimited Data</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-orange-400" />
                  <span>Easy to Approach</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-orange-400" />
                  <span>Free Setup</span>
                </div>
              </div>
            </div>

            {/* Right — Satellite + M Logo Orbital Scene */}
            <div className="reveal-right">
              <div className="hero-scene" ref={sceneRef} data-testid="hero-orbital-scene">
                {/* Concentric glowing arcs */}
                <div className="arc arc-3" />
                <div className="arc arc-2" />
                <div className="arc arc-1" />

                {/* M Logo at the center — parallax driven */}
                <img
                  ref={logoRef}
                  src={M_LOGO_URL}
                  alt="SSM VISION"
                  className="m-logo-core"
                />

                {/* Signal pulses radiating from center */}
                <div className="signal-pulse" />
                <div className="signal-pulse delay-1" />
                <div className="signal-pulse delay-2" />

                {/* Outer orbit with detailed satellite */}
                <div className="orbit">
                  <div className="satellite-detailed" title="Satellite">
                    <SatelliteIcon className="w-full h-full" />
                  </div>
                </div>

                {/* Inner orbit with smaller relay node */}
                <div className="orbit orbit-2">
                  <div className="satellite-detailed satellite-small">
                    <RelayIcon className="w-full h-full" />
                  </div>
                </div>
              </div>

              {/* Stats glass card under the orbital scene */}
              <div className="glass rounded-2xl p-5 mt-8 max-w-md mx-auto reveal reveal-d2" data-testid="hero-stats-card">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-3xl font-bold text-[#FFB347]">₹299</p>
                    <p className="text-xs text-white/60 uppercase tracking-wider mt-1">Starts</p>
                  </div>
                  <div className="border-x border-white/10">
                    <p className="text-3xl font-bold text-[#FFB347]">300</p>
                    <p className="text-xs text-white/60 uppercase tracking-wider mt-1">Mbps</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-[#FFB347]">2K+</p>
                    <p className="text-xs text-white/60 uppercase tracking-wider mt-1">Customers</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Flowing Wave Divider */}
        <FlowingWave />
      </section>

      {/* ============== FEATURED PLANS — Pop up on scroll ============== */}
      <section className="relative py-24 bg-[#070707]" data-testid="featured-plans-section">
        <div className="ambient-glow opacity-70" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16 reveal-zoom">
            <Badge className="bg-orange-500/10 border border-orange-500/30 text-orange-300 mb-4">
              Featured Packages
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Plans that <span className="bg-gradient-to-r from-[#FFD27A] to-[#FF5C00] bg-clip-text text-transparent">scale with you</span>
            </h2>
            <p className="text-lg text-white/60 max-w-xl mx-auto">
              Choose a package that fits your speed, budget and entertainment needs.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {featuredPlans.map((plan, planIdx) => (
              <Card
                key={plan.id}
                className={`reveal-zoom reveal-d${planIdx + 1} glass-orange border-0 overflow-hidden group hover:scale-[1.02] transition-all duration-500`}
                data-testid={`featured-plan-card-${planIdx}`}
              >
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <Badge className="bg-gradient-to-r from-[#FFB800] to-[#FF8C00] text-black px-4 py-1 text-sm mb-4 font-semibold">
                      {plan.tagline}
                    </Badge>
                    <h3 className="text-6xl font-bold bg-gradient-to-r from-[#FFD27A] to-[#FF5C00] bg-clip-text text-transparent mb-2">
                      {plan.speed}
                    </h3>
                    <p className="text-white/60">{plan.type}</p>
                  </div>

                  <div className="space-y-3 mb-6">
                    {plan.plans.map((pricing, idx) => (
                      <div key={idx} className="flex justify-between items-center p-4 bg-white/5 border border-white/10 rounded-xl backdrop-blur-sm">
                        <div>
                          <p className="font-semibold text-white">₹{pricing.monthly}/mo</p>
                          <p className="text-xs text-white/50">Valid for {pricing.validity}</p>
                        </div>
                        <Badge variant="outline" className="text-[#FFB347] border-[#FFB347]/60 bg-orange-500/10">
                          ₹{pricing.price}
                        </Badge>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2 mb-6">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-white/70 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-orange-400 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  {plan.otts && (
                    <div className="mb-6 p-3 bg-orange-500/10 border border-orange-500/20 rounded-xl">
                      <p className="text-xs font-semibold text-orange-200 mb-2 uppercase tracking-wider">Includes OTT</p>
                      <div className="flex flex-wrap gap-2">
                        {plan.otts.slice(0, 4).map((ott, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs bg-white/10 text-white/80 border-0">
                            {ott}
                          </Badge>
                        ))}
                        <Badge variant="secondary" className="text-xs bg-white/10 text-white/80 border-0">
                          +{plan.otts.length - 4} more
                        </Badge>
                      </div>
                    </div>
                  )}

                  <Link to="/contact">
                    <Button
                      className="w-full bg-gradient-to-r from-[#FF8C00] to-[#FFB800] hover:from-[#FF7700] hover:to-[#FFA600] text-black font-semibold shadow-lg hover:shadow-[0_8px_32px_-4px_rgba(255,140,0,0.6)]"
                      onClick={() => trackPlanSelection(plan.speed + ' ' + plan.type, plan.plans[0].price)}
                      data-testid={`featured-plan-subscribe-${planIdx}`}
                    >
                      Subscribe Now
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12 reveal">
            <Link to="/plans">
              <Button
                variant="outline"
                size="lg"
                className="border-orange-400/40 bg-white/5 text-orange-100 hover:bg-orange-500/10 hover:border-orange-400 backdrop-blur-md"
                onClick={() => trackButtonClick('Browse All Plans', 'Featured Plans Section')}
                data-testid="browse-all-plans-btn"
              >
                Browse All Plans
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ============== WHY CHOOSE US ============== */}
      <section className="relative py-24 bg-[#050505]" data-testid="why-choose-us-section">
        <FlowingWave reverse />
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16 reveal">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Why Choose <span className="bg-gradient-to-r from-[#FFD27A] to-[#FF5C00] bg-clip-text text-transparent">SSM VISION?</span>
            </h2>
            <p className="text-lg text-white/60">Built for streamers, families and remote teams.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {whyChooseUs.map((item, idx) => {
              const IconComponent = iconMap[item.icon];
              return (
                <Card
                  key={idx}
                  className={`glass reveal reveal-d${idx + 1} border-0 text-center hover:scale-[1.03] transition-transform duration-500 group`}
                  data-testid={`why-card-${idx}`}
                >
                  <CardContent className="p-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#FF8C00] to-[#FF5C00] flex items-center justify-center shadow-[0_8px_24px_-8px_rgba(255,140,0,0.8)] group-hover:scale-110 transition-transform">
                      {IconComponent && <IconComponent className="w-8 h-8 text-black" />}
                    </div>
                    <h3 className="text-xl font-semibold mb-3 text-white">{item.title}</h3>
                    <p className="text-white/60">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== OTT Marquee ============== */}
      <section className="relative py-20 bg-[#080808] overflow-hidden" data-testid="ott-section">
        <div className="container mx-auto px-4 text-center mb-10 reveal">
          <Badge className="bg-orange-500/10 border border-orange-500/30 text-orange-300 mb-4">
            Entertainment Included
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">18+ Premium OTT Platforms</h2>
          <p className="text-lg text-white/60">Unlimited binge-watching with our combo plans</p>
        </div>

        <div className="relative overflow-hidden mask-fade">
          <div className="flex animate-scroll-left space-x-12 items-center">
            {[...ottPlatformsWithLogos, ...ottPlatformsWithLogos, ...ottPlatformsWithLogos].map((ott, idx) => (
              <div key={idx} className="flex-shrink-0 glass rounded-xl px-6 py-4">
                <img src={ott.logo} alt={ott.name} className="h-12 w-auto object-contain" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== CTA ============== */}
      <section className="relative py-24 overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF5C00] via-[#FF8C00] to-[#FFB800]" />
        <div className="absolute inset-0 opacity-30 grain-overlay" />
        <div className="container mx-auto px-4 text-center relative z-10 reveal-zoom">
          <h2 className="text-4xl md:text-6xl font-bold mb-4 text-black">Ready to Get Started?</h2>
          <p className="text-lg md:text-xl mb-8 text-black/80">Join 2,000+ happy customers enjoying high-speed internet</p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-black text-[#FFB347] hover:bg-gray-900 px-10 py-6 text-lg font-semibold shadow-2xl"
              onClick={() => trackButtonClick('Contact Us Now', 'CTA Section')}
              data-testid="cta-contact-btn"
            >
              Contact Us Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

/* ===== Reusable flowing orange wave divider ===== */
const FlowingWave = ({ reverse = false }) => (
  <div className={`wave-flow ${reverse ? '-scale-y-100' : ''}`} aria-hidden="true">
    <svg
      className="wave-back"
      viewBox="0 0 1440 120"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0,40 C240,100 480,0 720,40 C960,80 1200,20 1440,40 L1440,120 L0,120 Z"
        fill="url(#waveBack)"
      />
      <defs>
        <linearGradient id="waveBack" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#FF5C00" stopOpacity="0.3" />
          <stop offset="50%" stopColor="#FF8C00" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#FFB800" stopOpacity="0.3" />
        </linearGradient>
      </defs>
    </svg>
    <svg
      viewBox="0 0 1440 120"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0,60 C240,20 480,100 720,60 C960,20 1200,100 1440,60 L1440,120 L0,120 Z"
        fill="url(#waveFront)"
      />
      <defs>
        <linearGradient id="waveFront" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#FFB800" />
          <stop offset="50%" stopColor="#FF8C00" />
          <stop offset="100%" stopColor="#FF5C00" />
        </linearGradient>
      </defs>
    </svg>
  </div>
);

export default Home;
